import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Customers from './pages/Customers';
import RoutesPage from './pages/Routes';
import StormMode from './pages/StormMode';
import ServiceHistory from './pages/ServiceHistory';
import IntakeForm from './pages/IntakeForm';
import RatesPage from './pages/RatesPage';
import SeasonRespond from './pages/SeasonRespond';
import Unsubscribe from './pages/Unsubscribe';
import SmsPolicy from './pages/SmsPolicy';
import AdminPanel from './pages/AdminPanel';
import Expenses from './pages/Expenses';
import Reports from './pages/Reports';
import Pricing from './pages/Pricing';
import LandingPage from './pages/LandingPage';
import Help from './pages/Help';
import JoinTeam from './pages/JoinTeam';
import MyRoutes from './pages/MyRoutes';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import DeleteRequest from './pages/DeleteRequest';

// Portal Pages
import CustomerLogin from './pages/portal/CustomerLogin';
import CustomerPortalSetup from './pages/portal/CustomerPortalSetup';
import CustomerForgotPassword from './pages/portal/CustomerForgotPassword';
import CustomerResetPassword from './pages/portal/CustomerResetPassword';
import CustomerDashboard from './pages/portal/CustomerDashboard';
import CustomerServices from './pages/portal/CustomerServices';
import CustomerInvoices from './pages/portal/CustomerInvoices';
import { PortalAuthProvider } from './context/PortalAuthContext';

// Components
import ScrollToTop from './components/ScrollToTop';
import ManifestSwitcher from './components/ManifestSwitcher';
import OfflineBanner from './components/OfflineBanner';
import UpdateBanner from './components/UpdateBanner';
import ProtectedRoute, { CUSTOMERS_ROLES, ADMIN_ROLES } from './components/ProtectedRoute';

// Super Admin Route Component
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  const { user } = useAuth();

  return (
    <div className="App">
      {/* Scrolls back to the top on every route change (bug 13). Sits inside the
          router, which index.js mounts above App, so useLocation resolves. */}
      <ScrollToTop />
      {/* Portal routes install as their own app (start_url /portal). */}
      <ManifestSwitcher />
      {/* Rendered above the routes so a lost connection is announced on every
          screen, not just the authenticated ones (bug 14). */}
      <OfflineBanner />
      {/* Offers a new version after a deploy; the update only runs on a tap. */}
      <UpdateBanner />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={user ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route
          path="/register"
          element={user ? <Navigate to="/dashboard" replace /> : <Register />}
        />
        <Route
          path="/forgot-password"
          element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
        />
        <Route
          path="/reset-password"
          element={user ? <Navigate to="/dashboard" replace /> : <ResetPassword />}
        />

        {/* Public legal pages - must be reachable without logging in */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/delete-account" element={<DeleteRequest />} />

        {/* Public Pricing Page */}
        <Route path="/pricing" element={<Pricing />} />

        {/* Public Intake Form - accessible without login */}
        <Route path="/new-customer/:companyCode" element={<IntakeForm />} />
        <Route path="/signup/:companyCode" element={<IntakeForm />} />

        {/* Public rates page for a company - accessible without login */}
        <Route path="/rates/:slug" element={<RatesPage />} />

        {/* The Yes/No links in the season email and text - no login, the
            token is the credential (pages/SeasonRespond.js) */}
        <Route path="/season/:token/:answer" element={<SeasonRespond />} />

        {/* The Unsubscribe link in SnowTrack's product email - no login, the
            signed token is the credential (pages/Unsubscribe.js) */}
        <Route path="/unsubscribe/:token" element={<Unsubscribe />} />

        {/* A contractor's SMS privacy policy and terms, registered with its
            texting campaign - no login (pages/SmsPolicy.js) */}
        <Route path="/sms-policy/:slug/:kind" element={<SmsPolicy />} />

        {/* Team Invitation - accessible without login */}
        <Route path="/join/:token" element={<JoinTeam />} />

        {/* Customer Portal Routes - wrapped in PortalAuthProvider */}
        <Route path="/portal/login" element={
          <PortalAuthProvider>
            <CustomerLogin />
          </PortalAuthProvider>
        } />
        <Route path="/portal/setup" element={
          <PortalAuthProvider>
            <CustomerPortalSetup />
          </PortalAuthProvider>
        } />
        <Route path="/portal/forgot-password" element={
          <PortalAuthProvider>
            <CustomerForgotPassword />
          </PortalAuthProvider>
        } />
        <Route path="/portal/reset-password" element={
          <PortalAuthProvider>
            <CustomerResetPassword />
          </PortalAuthProvider>
        } />
        <Route path="/portal" element={
          <PortalAuthProvider>
            <CustomerDashboard />
          </PortalAuthProvider>
        } />
        <Route path="/portal/services" element={
          <PortalAuthProvider>
            <CustomerServices />
          </PortalAuthProvider>
        } />
        <Route path="/portal/invoices" element={
          <PortalAuthProvider>
            <CustomerInvoices />
          </PortalAuthProvider>
        } />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <ProtectedRoute roles={CUSTOMERS_ROLES}>
              <Customers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/routes"
          element={
            <ProtectedRoute>
              <RoutesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <ServiceHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/storm-mode"
          element={
            <ProtectedRoute>
              <StormMode />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses"
          element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <Expenses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/docs"
          element={
            <ProtectedRoute>
              <Help />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-routes"
          element={
            <ProtectedRoute>
              <MyRoutes />
            </ProtectedRoute>
          }
        />

        {/* Super Admin Routes */}
        <Route
          path="/admin"
          element={
            <SuperAdminRoute>
              <AdminPanel />
            </SuperAdminRoute>
          }
        />

        {/* Default redirect */}
        <Route
          path="/"
          element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />}
        />

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="not-found">
              <h1>404</h1>
              <p>Page not found</p>
              <a href="/">Go home</a>
            </div>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
